import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, MenuIcon, XIcon, ArrowUpRightIcon, FlameIcon, TreesIcon, LeafIcon, ZapIcon, CoffeeIcon, BarChart3Icon, GraduationCapIcon, LandmarkIcon, BriefcaseIcon, LayersIcon, FactoryIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { navigate } from '../utils/navigate';
import { MAILTO } from '../utils/mailto';
interface SolutionItem {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}
const solutions: SolutionItem[] = [{
  id: 'ghg',
  icon: <BarChart3Icon className="w-5 h-5" data-id="element-672" />,
  title: 'GHG Accounting & ESG',
  description: 'Enterprise GHG reporting, IFRS, CSRD, and CBAM compliance'
}, {
  id: 'coffee',
  icon: <CoffeeIcon className="w-5 h-5" data-id="element-673" />,
  title: 'Coffee Traceability',
  description: 'Farm-to-cup supply chain verification and EUDR compliance'
}, {
  id: 'cookstoves',
  icon: <FlameIcon className="w-5 h-5" data-id="element-674" />,
  title: 'Cookstove Carbon Credits',
  description: 'IoT-enabled DMRV for clean cooking emission reductions'
}, {
  id: 'forestry',
  icon: <TreesIcon className="w-5 h-5" data-id="element-675" />,
  title: 'Forestry & REDD+',
  description: 'Satellite-verified forest carbon monitoring and credits'
}, {
  id: 'agroforestry',
  icon: <LeafIcon className="w-5 h-5" data-id="element-676" />,
  title: 'Agroforestry & Soil Carbon',
  description: 'Soil carbon sequestration measurement and verification'
}, {
  id: 'energy',
  icon: <ZapIcon className="w-5 h-5" data-id="element-677" />,
  title: 'Renewable Energy',
  description: 'Carbon credit verification for solar and mini-grid projects'
}];
interface TrainingItem {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}
const trainings: TrainingItem[] = [{
  id: 'courses',
  icon: <LayersIcon className="w-5 h-5" data-id="element-678" />,
  title: 'ESG Foundations',
  description: 'Core ESG & GHG accounting for all industries'
}, {
  id: 'courses',
  icon: <LandmarkIcon className="w-5 h-5" data-id="element-679" />,
  title: 'Banking & Financial Institutions',
  description: 'PCAF, IFRS S1/S2, climate risk in credit assessment'
}, {
  id: 'courses',
  icon: <FlameIcon className="w-5 h-5" data-id="element-680" />,
  title: 'Oil & Gas Climate Compliance',
  description: 'Methane accounting, Scope 3, decarbonization pathways'
}, {
  id: 'courses',
  icon: <FactoryIcon className="w-5 h-5" data-id="element-681" />,
  title: 'Manufacturing & Export',
  description: 'CBAM readiness, supply chain Scope 3, carbon border tax'
}, {
  id: 'courses',
  icon: <LeafIcon className="w-5 h-5" data-id="element-682" />,
  title: 'Agriculture & Forestry (AFOLU)',
  description: 'Soil carbon, dMRV principles, Article 6 carbon markets'
}, {
  id: 'courses',
  icon: <BriefcaseIcon className="w-5 h-5" data-id="element-683" />,
  title: 'Executive & Board Certification',
  description: 'Climate governance, fiduciary duty, litigation risk'
}];
export function Navbar({
  currentPage,
  onNavigate
}: {
  currentPage: string;
  onNavigate: (page: string) => void;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [trainingsOpen, setTrainingsOpen] = useState(false);
  const [mobileTrainingsOpen, setMobileTrainingsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const trainingsDropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trainingsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setSolutionsOpen(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setSolutionsOpen(false), 150);
  };
  const handleTrainingsMouseEnter = () => {
    if (trainingsTimeoutRef.current) clearTimeout(trainingsTimeoutRef.current);
    setTrainingsOpen(true);
  };
  const handleTrainingsMouseLeave = () => {
    trainingsTimeoutRef.current = setTimeout(() => setTrainingsOpen(false), 150);
  };
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (trainingsTimeoutRef.current) clearTimeout(trainingsTimeoutRef.current);
    };
  }, []);
  const isSolutionPage = solutions.some(s => s.id === currentPage);
  const isTrainingPage = currentPage === 'courses';
  return <nav className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-md border-b border-carbon-geo" data-id="element-684">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-685">
        <div className="flex items-center justify-between h-16 lg:h-20" data-id="element-686">
          {/* Logo */}
          <button onClick={() => onNavigate('home')} className="flex-shrink-0" aria-label="CarbonSafe Home" data-id="element-687">

            <img src="https://f000.backblazeb2.com/file/jpgshared/f2RRyoGc" alt="CarbonSafe" className="h-9 lg:h-10 w-auto" data-id="element-688" />

          </button>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1" data-id="element-689">
            {/* Solutions Dropdown */}
            <div ref={dropdownRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-id="element-690">

              <button className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isSolutionPage || solutionsOpen ? 'text-carbon-bright' : 'text-carbon-deep hover:text-carbon-bright'}`} onClick={() => setSolutionsOpen(!solutionsOpen)} aria-expanded={solutionsOpen} aria-haspopup="true" data-id="element-691">

                Solutions
                <ChevronDownIcon className={`w-3.5 h-3.5 opacity-60 transition-transform duration-200 ${solutionsOpen ? 'rotate-180' : ''}`} data-id="element-692" />

              </button>

              <AnimatePresence data-id="element-693">
                {solutionsOpen && <motion.div initial={{
                opacity: 0,
                y: 8,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 8,
                scale: 0.98
              }} transition={{
                duration: 0.18,
                ease: [0.22, 1, 0.36, 1]
              }} className="absolute top-full left-0 mt-2 w-[380px] bg-white rounded-xl shadow-xl border border-carbon-geo overflow-hidden" data-id="element-694">

                    <div className="p-2" data-id="element-695">
                      <p className="px-3 pt-2 pb-2 text-[11px] font-bold uppercase tracking-wider text-carbon-mid/60" data-id="element-696">
                        DMRV Opportunities | Uganda
                      </p>
                      {solutions.map(item => <button key={item.id} onClick={() => {
                    onNavigate(item.id);
                    setSolutionsOpen(false);
                  }} className={`flex items-start gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors ${currentPage === item.id ? 'bg-carbon-bright/5' : 'hover:bg-carbon-bright/5'}`} data-id="element-697">

                          <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5 ${currentPage === item.id ? 'bg-carbon-bright/15 text-carbon-bright' : 'bg-carbon-geo/50 text-carbon-mid'}`} data-id="element-698">

                            {item.icon}
                          </div>
                          <div className="min-w-0" data-id="element-699">
                            <p className={`text-sm font-semibold ${currentPage === item.id ? 'text-carbon-bright' : 'text-carbon-deep'}`} data-id="element-700">

                              {item.title}
                            </p>
                            <p className="text-xs text-carbon-mid mt-0.5 leading-snug" data-id="element-701">
                              {item.description}
                            </p>
                          </div>
                        </button>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Corporate Training — simple link, no dropdown */}
            <button onClick={() => onNavigate('courses')} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isTrainingPage ? 'text-carbon-bright' : 'text-carbon-deep hover:text-carbon-bright'}`} data-id="element-702">

              <GraduationCapIcon className="w-3.5 h-3.5 opacity-70" data-id="element-703" />
              Training
            </button>

            {/* Other nav links */}
            <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-carbon-deep hover:text-carbon-bright transition-colors rounded-lg" data-id="element-704">
              Resources
            </button>
            <button onClick={() => onNavigate('about')} className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${currentPage === 'about' ? 'text-carbon-bright' : 'text-carbon-deep hover:text-carbon-bright'}`} data-id="element-705">

              About
            </button>
          </div>

          {/* Desktop Right */}
          <div className="hidden lg:flex items-center gap-3" data-id="element-706">
            <a href="https://dmrv.carbonsafe.earth" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-carbon-deep hover:text-carbon-bright transition-colors" data-id="element-707">

              Log in
            </a>
            <a href={MAILTO.expert} className="inline-flex items-center gap-2 px-5 py-2.5 bg-carbon-bright text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors" data-id="element-708">

              Talk to Expert
              <ArrowUpRightIcon className="w-4 h-4" data-id="element-709" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden p-2 text-carbon-deep" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} data-id="element-710">

            {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-711" /> : <MenuIcon className="w-6 h-6" data-id="element-712" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-713">
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeInOut'
      }} className="lg:hidden overflow-hidden bg-white border-b border-carbon-geo" data-id="element-714">

            <div className="px-4 py-4 space-y-1" data-id="element-715">
              {/* Solutions Accordion */}
              <button onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)} className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-carbon-deep rounded-lg" data-id="element-716">

                Solutions
                <ChevronDownIcon className={`w-4 h-4 opacity-60 transition-transform duration-200 ${mobileSolutionsOpen ? 'rotate-180' : ''}`} data-id="element-717" />

              </button>

              <AnimatePresence data-id="element-718">
                {mobileSolutionsOpen && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2
            }} className="overflow-hidden" data-id="element-719">

                    <div className="pl-3 pr-1 pb-2 space-y-0.5" data-id="element-720">
                      <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-carbon-mid/50" data-id="element-721">
                        DMRV Opportunities
                      </p>
                      {solutions.map(item => <button key={item.id} onClick={() => {
                  onNavigate(item.id);
                  setMobileOpen(false);
                  setMobileSolutionsOpen(false);
                }} className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-left transition-colors ${currentPage === item.id ? 'bg-carbon-bright/5 text-carbon-bright' : 'text-carbon-deep hover:bg-carbon-bright/5'}`} data-id="element-722">

                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${currentPage === item.id ? 'bg-carbon-bright/15 text-carbon-bright' : 'bg-carbon-geo/50 text-carbon-mid'}`} data-id="element-723">

                            {item.icon}
                          </div>
                          <span className="text-sm font-semibold" data-id="element-724">
                            {item.title}
                          </span>
                        </button>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Corporate Training — simple link, no accordion */}
              <button onClick={() => {
            onNavigate('courses');
            setMobileOpen(false);
          }} className={`flex items-center gap-2 w-full px-3 py-3 text-sm font-medium rounded-lg ${isTrainingPage ? 'text-carbon-bright' : 'text-carbon-deep'}`} data-id="element-725">

                <GraduationCapIcon className="w-4 h-4 opacity-70" data-id="element-726" />
                Training
              </button>

              <button className="flex items-center w-full px-3 py-3 text-sm font-medium text-carbon-deep rounded-lg" data-id="element-727">
                Resources
              </button>
              <button onClick={() => {
            onNavigate('about');
            setMobileOpen(false);
          }} className={`flex items-center w-full px-3 py-3 text-sm font-medium rounded-lg ${currentPage === 'about' ? 'text-carbon-bright' : 'text-carbon-deep'}`} data-id="element-728">

                About
              </button>

              <hr className="border-carbon-geo my-2" data-id="element-729" />

              <a href="https://dmrv.carbonsafe.earth" target="_blank" rel="noopener noreferrer" className="block px-3 py-3 text-sm font-medium text-carbon-deep" data-id="element-730">

                Log in
              </a>
              <a href={MAILTO.expert} className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-carbon-bright text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors" data-id="element-731">

                Talk to Expert
                <ArrowUpRightIcon className="w-4 h-4" data-id="element-732" />
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}